import React from 'react';
import { useNavigate } from 'react-router';
import { GlobalLoader } from '@shared/components';
import { useOrgEmployeeDepartments } from './hooks/useOrgEmployeeDepartments';
import { useI18n } from '@shared/i18n';
import type { EmployeeLite } from '@shared/types';
import styles from './DepartmentsSummary.module.css';

/**
 * DepartmentsSummary component
 * Displays a summarized view of user's departments with quick access
 */
const DepartmentsSummary: React.FC = (): React.ReactElement => {
    const navigate = useNavigate();
    const { orgEmployee, isLoading, error } = useOrgEmployeeDepartments();
    const { t } = useI18n();

    const handleViewAllDepartments = (): void => {
        navigate('/departments');
    };

    const formatVacancyRate = (rate: number | undefined): string => {
        if (rate === undefined || rate === null) return '-';
        const value = rate / 100;
        return `${value % 1 === 0 ? value.toFixed(0) : value.toFixed(2)}%`;
    };

    const DepartmentCard = ({
        employee,
    }: {
        employee: EmployeeLite;
    }): React.ReactElement => (
        <div className={styles.departmentCard}>
            <div className={styles.departmentCardHeader}>
                <h3 className={styles.departmentCardTitle}>
                    {employee.departmentName ??
                        t('departments.departmentCardTitle', {
                            id: employee.id.slice(0, 8),
                        })}
                </h3>
            </div>
            <div className={styles.departmentCardBody}>
                <div className={styles.departmentInfoRow}>
                    <span className={styles.departmentInfoLabel}>
                        {t('departments.position')}:
                    </span>
                    <span className={styles.departmentInfoValue}>
                        {employee.positionName ?? '-'}
                    </span>
                </div>
                {employee.vacancyRate !== undefined && (
                    <div className={styles.departmentInfoRow}>
                        <span className={styles.departmentInfoLabel}>
                            {t('departments.vacancyRate')}:
                        </span>
                        <span className={styles.departmentInfoValue}>
                            {formatVacancyRate(employee.vacancyRate)}
                        </span>
                    </div>
                )}
                <div className={styles.departmentInfoRow}>
                    <span className={styles.departmentInfoLabel}>
                        {t('departments.name')}:
                    </span>
                    <span className={styles.departmentInfoValue}>
                        {employee.name}
                    </span>
                </div>
            </div>
            <div className={styles.departmentCardFooter}>
                <button
                    type="button"
                    className={styles.viewDetailsButton}
                    onClick={handleViewAllDepartments}
                >
                    {t('departments.viewAllDetails')}
                </button>
            </div>
        </div>
    );

    if (isLoading) {
        return <GlobalLoader className={styles.loadingSpinner} />;
    }

    if (error) {
        return <p className={styles.errorMessage}>{error}</p>;
    }

    if (!orgEmployee || orgEmployee.employees.length === 0) {
        return (
            <p className={styles.emptyMessage}>
                {t('departments.emptyMessage')}
            </p>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.cardsGrid}>
                {orgEmployee.employees.map((employee): React.ReactElement => (
                    <DepartmentCard
                        key={employee.id}
                        employee={employee}
                    />
                ))}
            </div>
        </div>
    );
};

export default DepartmentsSummary;
